import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Activity, AlertTriangle, CheckCircle2 } from 'lucide-react';

/**
 * SleepoverWatchdogTimelineTile
 *
 * Renders the last N (default 8) sleep-over watchdog snapshots in a compact
 * row. Pulls from `/api/admin/sleepover/watchdog/snapshots` — purely surfaces
 * data the watchdog already writes on its 6-hour cadence.
 *
 * Feature-flag tolerant: hides when `sleepover_billing_enabled` is OFF (403).
 */
export function SleepoverWatchdogTimelineTile({ limit = 8, className = '' }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ count: 0, alert_count: 0, latest: null });
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/sleepover/watchdog/snapshots?limit=${limit}`);
      const data = r.data?.data || {};
      setRows(data.rows || []);
      setSummary(data.summary || { count: 0, alert_count: 0, latest: null });
      setEnabled(true);
    } catch (e) {
      if (e.response?.status === 403) setEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [limit]);

  if (!enabled) return null;

  const formatAt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const dotClassFor = (snap) => {
    if (snap.overall_status === 'OK') return 'bg-green-500';
    const sev = snap.highest_severity;
    if (sev === 'HIGH') return 'bg-red-500';
    if (sev === 'MEDIUM') return 'bg-amber-500';
    return 'bg-slate-400';
  };

  const orderedRows = [...rows].reverse(); // oldest → newest, left to right

  const headlineTone =
    summary.alert_count > 0 ? 'border-amber-300 bg-amber-50/40' : 'border-green-300 bg-green-50/40';

  return (
    <Card className={`${headlineTone} ${className}`} data-testid="sleepover-watchdog-timeline-tile">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Activity className="w-4 h-4" /> Sleep-Over Validation Watchdog
          <Badge variant="outline" className="text-[9px] ml-auto">last {summary.count} ticks</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {loading ? (
          <div className="text-xs text-muted-foreground">Loading watchdog snapshots…</div>
        ) : rows.length === 0 ? (
          <div className="text-xs text-muted-foreground">
            No watchdog ticks yet — the scheduler runs every 6 h and the first
            tick fires 60 s after backend startup. You can trigger one manually
            via{' '}
            <code className="text-[10px]">POST /api/admin/sleepover/watchdog/run-now</code>.
          </div>
        ) : (
          <>
            <div
              className="flex items-end gap-1.5 overflow-x-auto pb-1"
              data-testid="sleepover-watchdog-timeline-row"
            >
              {orderedRows.map((s) => (
                <div
                  key={s.id}
                  className="flex flex-col items-center min-w-[44px]"
                  title={
                    `${formatAt(s.generated_at)}\n` +
                    `status: ${s.overall_status}\n` +
                    `tone: ${s.health?.tone}\n` +
                    `mismatch: ${s.health?.mismatch_shifts || 0} · pending: ${s.health?.pending_adjustments_count || 0}\n` +
                    `latency: ${s.latency_ms} ms` +
                    (s.alerts?.length ? `\nalerts: ${s.alerts.map((a) => a.code).join(', ')}` : '')
                  }
                  data-testid={`watchdog-tick-${s.id}`}
                >
                  <div className={`w-3 h-3 rounded-full ${dotClassFor(s)}`} />
                  <div className="text-[9px] text-muted-foreground mt-1 tabular-nums">
                    {s.latency_ms}ms
                  </div>
                  <div className="text-[8px] text-muted-foreground tabular-nums">
                    {formatAt(s.generated_at).split(',')[1]?.trim() || ''}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between text-[11px] pt-1 border-t">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                {summary.alert_count > 0 ? (
                  <>
                    <AlertTriangle className="w-3 h-3 text-amber-600" />
                    {summary.alert_count} of {summary.count} ticks raised an alert
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-3 h-3 text-green-600" />
                    All {summary.count} ticks within thresholds
                  </>
                )}
              </div>
              {summary.latest && (
                <span
                  className="text-[10px] text-muted-foreground tabular-nums"
                  data-testid="watchdog-latest-tick-status"
                >
                  latest: {summary.latest.overall_status} · {summary.latest.health?.tone} · {summary.latest.latency_ms}ms
                </span>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
